import { useState } from 'react'
import { openBillingPortal, startCheckout } from '../lib/api'
import { languageFromHost, languageOf } from '../lib/languages'
import { useStore } from '../state/store'
import { PRICE_PER_MONTH } from '../views/SubscribeView'

/**
 * What you are paying, and how to start or stop.
 *
 * This card exists because of a hole with a price on it. `startCheckout` was
 * reachable from exactly one screen — the paywall — and the paywall is shown
 * only to people who are NOT entitled to this language. Anyone inside their
 * free week is entitled, so they never saw it: a customer who decided on day
 * two that they wanted to pay had no way to do so until their access lapsed on
 * day eight, by which point they had already been locked out once. Every trial
 * had to fail before it could convert.
 *
 * So the subscribe button lives here too, where somebody who is happily using
 * the thing can find it.
 */

const DAY_MS = 24 * 60 * 60 * 1000

/** "3 March" — no year, because everything here is within weeks. */
function shortDate(at: number): string {
  return new Date(at).toLocaleDateString(undefined, { day: 'numeric', month: 'long' })
}

/** Days remaining, rounded up: half a day left is still "1 day", never "0". */
function daysLeft(at: number, now = Date.now()): number {
  return Math.max(0, Math.ceil((at - now) / DAY_MS))
}

export function BillingCard({
  standing,
  billing = null,
}: {
  standing: { source: string; expiresAt: number | null } | null
  /** What Stripe sent us back with, if this is the return leg of a checkout. */
  billing?: 'done' | 'cancelled' | 'subscribe' | null
}) {
  /*
   * The HOST language, not the one currently selected in the app.
   *
   * `standing` describes what this site sells — the server derives it from the
   * hostname — and the two can differ: the language picker changes what you are
   * drilling without moving you to another subdomain. Naming the selected one
   * here would put "Subscription — Italian" above a Mandarin subscription.
   */
  const code = languageFromHost()
  const lang = languageOf(code ?? undefined)
  const { data } = useStore()
  const token = data.sync.token ?? ''
  const [busy, setBusy] = useState<'checkout' | 'portal' | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Nothing honest to say. Signed out, or on a host that names no language, or
  // holding nothing here — in which case the paywall has already taken over.
  if (!token || !standing || !code) return null

  async function go(what: 'checkout' | 'portal') {
    setBusy(what)
    setError(null)
    const result = what === 'checkout' ? await startCheckout(token) : await openBillingPortal(token)
    if (!result.ok) {
      setBusy(null)
      setError(result.error)
      return
    }
    // Full-page, not a new tab: the return trip carries ?billing=done and has to
    // land back in this app, and a popup would be blocked about half the time.
    window.location.href = result.url
  }

  const ends = standing.expiresAt
  const carries = ends !== null && ends - Date.now() > 48 * 60 * 60 * 1000

  return (
    <div className="card">
      <div className="stat-label" style={{ marginBottom: '0.7rem' }}>
        Subscription — {lang.name}
      </div>

      {/* Say what happened. Returning from a payment to a page that mentions it
          nowhere is indistinguishable from a payment that failed. */}
      {billing === 'done' && (
        <div className="notice" style={{ marginBottom: '0.8rem' }}>
          Thank you — {lang.name} is on your account. A receipt is on its way from Stripe.
        </div>
      )}
      {billing === 'cancelled' && (
        <div className="notice" style={{ marginBottom: '0.8rem' }}>
          Checkout was closed and nothing was charged. Everything is exactly as it was.
        </div>
      )}

      {error && (
        <div className="notice is-error" style={{ marginBottom: '0.8rem' }}>
          {error}
        </div>
      )}

      {standing.source === 'comped' && (
        <p className="english" style={{ margin: 0 }}>
          Every language, free, with our compliments. There is nothing to pay and nothing to cancel.
        </p>
      )}

      {standing.source === 'trial' && ends !== null && (
        <>
          <p className="english" style={{ marginTop: 0 }}>
            You are on the free week. It ends on <strong>{shortDate(ends)}</strong> —{' '}
            {daysLeft(ends)} {daysLeft(ends) === 1 ? 'day' : 'days'} left — and then {lang.name}{' '}
            simply stops. Your islands and your practice history are kept either way.
          </p>
          <button
            type="button"
            className="btn is-primary"
            disabled={busy !== null}
            onClick={() => void go('checkout')}
          >
            {busy === 'checkout' ? 'Opening checkout…' : `Keep ${lang.name} — $${PRICE_PER_MONTH} a month`}
          </button>
          <p className="muted" style={{ fontSize: '0.84rem', margin: '0.6rem 0 0' }}>
            {carries ? (
              <>
                Nothing is charged until <strong>{shortDate(ends)}</strong> — subscribing now keeps
                the rest of your free week rather than cutting it short.
              </>
            ) : (
              <>Billing starts today, since there is less than a day of the trial left.</>
            )}{' '}
            Stripe handles the card; it never touches this site.
          </p>
        </>
      )}

      {standing.source === 'stripe' && (
        <>
          <p className="english" style={{ marginTop: 0 }}>
            Subscribed — ${PRICE_PER_MONTH} a month.
            {ends !== null && <> Paid through <strong>{shortDate(ends)}</strong>.</>}
          </p>
          <button
            type="button"
            className="btn"
            disabled={busy !== null}
            onClick={() => void go('portal')}
          >
            {busy === 'portal' ? 'Opening…' : 'Manage billing'}
          </button>
          <p className="muted" style={{ fontSize: '0.84rem', margin: '0.6rem 0 0' }}>
            Cards, receipts and cancelling are all on Stripe's own page. Cancelling leaves{' '}
            {lang.name} working until the date above.
          </p>
        </>
      )}

      {/* 'manual' and 'invite' — someone gave it to you rather than you buying it.
          Offering a checkout button here would be asking for money already paid. */}
      {standing.source !== 'comped' &&
        standing.source !== 'trial' &&
        standing.source !== 'stripe' && (
          <p className="english" style={{ margin: 0 }}>
            {lang.name} was added to your account directly.
            {ends === null ? ' It does not expire.' : ` It runs until ${shortDate(ends)}.`}
          </p>
        )}
    </div>
  )
}
